import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { Product } from "data/data";

interface UIContextValue {
  // Product customization sheet
  customizingProduct: Product | null;
  isCustomizationOpen: boolean;
  openCustomization: (product: Product) => void;
  closeCustomization: () => void;

  // Auth modal
  isAuthModalOpen: boolean;
  authModalTab: "login" | "signup";
  openAuthModal: (tab?: "login" | "signup") => void;
  closeAuthModal: () => void;
}

const UIContext = createContext<UIContextValue>({
  customizingProduct: null,
  isCustomizationOpen: false,
  openCustomization: () => {},
  closeCustomization: () => {},
  isAuthModalOpen: false,
  authModalTab: "login",
  openAuthModal: () => {},
  closeAuthModal: () => {},
});

export const UIProvider = ({ children }: { children: React.ReactNode }) => {
  const [customizingProduct, setCustomizingProduct] = useState<Product | null>(null);
  const [isCustomizationOpen, setIsCustomizationOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalTab, setAuthModalTab] = useState<"login" | "signup">("login");

  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const openCustomization = useCallback((product: Product) => {
    // Cancel any pending close cleanup
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setCustomizingProduct(product);
    setIsCustomizationOpen(true);
  }, []);

  const closeCustomization = useCallback(() => {
    setIsCustomizationOpen(false);
    closeTimeoutRef.current = setTimeout(() => setCustomizingProduct(null), 300);
  }, []);

  const openAuthModal = useCallback((tab: "login" | "signup" = "login") => {
    setAuthModalTab(tab);
    setIsAuthModalOpen(true);
  }, []);

  const closeAuthModal = useCallback(() => {
    setIsAuthModalOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      customizingProduct,
      isCustomizationOpen,
      openCustomization,
      closeCustomization,
      isAuthModalOpen,
      authModalTab,
      openAuthModal,
      closeAuthModal,
    }),
    [customizingProduct, isCustomizationOpen, isAuthModalOpen, authModalTab]
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export const useUI = () => useContext(UIContext);
